import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  CheckCircle2,
  CircleAlert,
  CircleDashed,
  Loader2,
  Pencil,
  Plus,
  Save,
  Send,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useControlCentreGate } from '../hooks/useControlCentreGate';
import { useAgentTasks } from '../hooks/useAgentTasks';
import { useTasks } from '../hooks/useTasks';
import { useDayFocus } from '../hooks/useDayFocus';
import { callConfirmBrowserTaskActions, callRunAgentTask, callScheduleAgentTask, callSendAgentTaskMessage } from '../services/functions';
import { syncAgentTasksFromSources } from '../services/agentTasks';
import type { AgentTask, AgentTaskStatus, DayFocus, TaskPlan } from '../types';

const STATUS_ORDER: AgentTaskStatus[] = ['active', 'pending', 'failed', 'completed'];

const STATUS_LABEL: Record<AgentTaskStatus, string> = {
  pending: 'Pending',
  active: 'Active',
  completed: 'Completed',
  failed: 'Failed',
};

const STATUS_ICON: Record<AgentTaskStatus, React.ReactNode> = {
  pending: <CircleDashed size={14} />,
  active: <Loader2 size={14} className="animate-spin" />,
  completed: <CheckCircle2 size={14} />,
  failed: <CircleAlert size={14} />,
};

function reasonCopy(reason: string): { title: string; body: string } {
  switch (reason) {
    case 'build_disabled':
      return {
        title: 'Agent Control Centre is not included in this build',
        body: 'This app build is running classic Flank mode. Re-enable when the feature build flag is turned on.',
      };
    case 'backend_disabled':
      return {
        title: 'Agent Control Centre is currently disabled',
        body: 'Backend capability is off for this environment.',
      };
    case 'kill_switch_active':
      return {
        title: 'Agent Control Centre is temporarily paused',
        body: 'A safety kill switch is active. Please try again later.',
      };
    case 'not_allowlisted':
      return {
        title: 'Agent Control Centre is in pilot mode',
        body: 'This account is not currently in the rollout allowlist.',
      };
    case 'user_disabled':
      return {
        title: 'Agent Control Centre is disabled for this account',
        body: 'Enable the feature in your profile flags to access this page.',
      };
    case 'tier_ineligible':
      return {
        title: 'Agent Control Centre is not in your current plan',
        body: 'This capability is gated by account tier in the current rollout policy.',
      };
    case 'backend_check_failed':
      return {
        title: 'Could not validate Agent Control Centre access',
        body: 'There was a temporary issue checking feature gates. Please refresh and retry.',
      };
    default:
      return {
        title: 'Agent Control Centre is unavailable',
        body: 'This feature is currently unavailable.',
      };
  }
}

function sortTasksForSidebar(tasks: AgentTask[]): AgentTask[] {
  const byStatusRank = new Map<AgentTaskStatus, number>(STATUS_ORDER.map((status, index) => [status, index]));
  return [...tasks].sort((a, b) => {
    const rankA = byStatusRank.get(a.status) ?? 99;
    const rankB = byStatusRank.get(b.status) ?? 99;
    if (rankA !== rankB) return rankA - rankB;
    const aUpdated = a.updatedAt?.toMillis?.() ?? 0;
    const bUpdated = b.updatedAt?.toMillis?.() ?? 0;
    return bUpdated - aUpdated;
  });
}

function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateKeyFromTimestamp(value: TaskPlan['createdAt'] | DayFocus['createdAt']): string {
  if (value?.toDate) return toDateKey(value.toDate());
  const seconds = (value as unknown as { seconds?: number })?.seconds ?? Date.now() / 1000;
  return toDateKey(new Date(seconds * 1000));
}

function summarizeActionItems(items: Array<{ text: string }>): string {
  const preview = items
    .map(item => item.text.trim())
    .filter(Boolean)
    .slice(0, 3)
    .join(' | ');
  return preview;
}

function inferPlannedToolCalls(task: AgentTask): string[] {
  const text = `${task.title} ${task.description}`.toLowerCase();
  if (task.runtime?.route === 'browser') {
    const planned = ['browser_preflight', 'browser_page_scan', 'browser_screenshot_capture', 'browser_live_executor', 'browser_plan'];
    if (/\b(submit|purchase|buy|pay|checkout|delete|remove account|confirm booking|book now|reserve now)\b/.test(text)) {
      planned.push('confirmation_gate');
    }
    return planned;
  }
  if (task.runtime?.route && task.runtime.route !== 'api_mcp') {
    return ['voice_or_external_rail'];
  }

  const planned = ['web_search'];
  if (/\b(restaurant|golf|hotel|cafe|coffee|dinner|lunch|breakfast|cuisine)\b/.test(text)) {
    planned.push('place_search');
  }
  if (/\bto\s+[a-z]|in\s+[a-z]|near\s+[a-z]/.test(text)) {
    planned.push('wikipedia_lookup');
  }
  planned.push('build_research_brief');
  return Array.from(new Set(planned));
}

export default function ControlCentrePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, updateFeatures } = useProfile(user?.uid ?? null);
  const { gate, loading: gateLoading, refresh } = useControlCentreGate(user?.uid ?? null, profile);
  const { allTasks: planningTasks, loading: planningLoading } = useTasks(user?.uid ?? null);
  const { allFocuses, loading: dayFocusLoading } = useDayFocus(user?.uid ?? null);
  const {
    tasks,
    loading,
    selectedTaskId,
    setSelectedTaskId,
    createTask,
    updateTaskDetails,
    deleteTask,
    updateStatus,
    thread,
    threadLoading,
    toolCalls,
    toolCallsLoading,
  } = useAgentTasks(user?.uid ?? null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [sendBusy, setSendBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [taskPendingDelete, setTaskPendingDelete] = useState<AgentTask | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; tone: 'error' | 'info' } | null>(null);
  const syncInFlightRef = useRef(false);

  const todayKey = useMemo(() => toDateKey(new Date()), []);
  const sortedTasks = useMemo(() => sortTasksForSidebar(tasks), [tasks]);
  const selectedTask = useMemo(
    () => sortedTasks.find(task => task.id === selectedTaskId) ?? null,
    [selectedTaskId, sortedTasks]
  );
  const plannedToolCalls = useMemo(
    () => (selectedTask ? inferPlannedToolCalls(selectedTask) : []),
    [selectedTask]
  );
  const sourceTasksForSync = useMemo(() => {
    const taskSources = planningTasks
      .filter(task => dateKeyFromTimestamp(task.createdAt) === todayKey && task.status === 'active')
      .map(task => ({
        sourceKey: `task:${task.id}`,
        sourceId: task.id,
        sourceDate: todayKey,
        kind: 'task' as const,
        title: task.title,
        description: task.summary?.trim() || summarizeActionItems(task.actionItems),
      }));

    const dayFocusSources = allFocuses
      .filter(focus => focus.focusDate === todayKey && focus.status === 'active')
      .map(focus => ({
        sourceKey: `day_focus:${focus.id}`,
        sourceId: focus.id,
        sourceDate: todayKey,
        kind: 'day_focus' as const,
        title: focus.title,
        description: (focus.summary ?? '').trim() || summarizeActionItems(focus.actionItems),
      }));

    return [...taskSources, ...dayFocusSources];
  }, [allFocuses, planningTasks, todayKey]);

  useEffect(() => {
    if (!selectedTask) {
      setIsEditingTask(false);
      setEditTitle('');
      setEditDescription('');
      return;
    }
    // Only sync from Firestore when NOT actively editing — prevents
    // realtime subscription updates from wiping what the user is typing.
    if (!isEditingTask) {
      setEditTitle(selectedTask.title);
      setEditDescription(selectedTask.description);
    }
  }, [selectedTask, isEditingTask]);

  useEffect(() => {
    if (!gate.available) return;
    if (!user?.uid) return;
    if (loading || planningLoading || dayFocusLoading) return;
    if (!sourceTasksForSync.length) return;
    if (syncInFlightRef.current) return;

    syncInFlightRef.current = true;
    void syncAgentTasksFromSources(user.uid, sourceTasksForSync, tasks)
      .catch(() => {})
      .finally(() => {
        syncInFlightRef.current = false;
      });
  }, [
    dayFocusLoading,
    gate.available,
    loading,
    planningLoading,
    sourceTasksForSync,
    tasks,
    user?.uid,
  ]);

  async function handleCreateTask(event: FormEvent) {
    event.preventDefault();
    if (createBusy) return;
    setCreateBusy(true);
    setFeedback(null);
    try {
      const createdId = await createTask({ title: createTitle, description: createDescription });
      if (!createdId) {
        setFeedback({ message: 'Please add a task title first.', tone: 'error' });
      } else {
        setShowCreateModal(false);
        setCreateTitle('');
        setCreateDescription('');
      }
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Could not create task right now.';
      setFeedback({ message, tone: 'error' });
    } finally {
      setCreateBusy(false);
    }
  }

  async function handleSendMessage(event: FormEvent) {
    event.preventDefault();
    if (!messageDraft.trim() || sendBusy || !selectedTask) return;
    const draft = messageDraft.trim();
    setSendBusy(true);
    setMessageDraft('');
    setFeedback(null);
    try {
      await callSendAgentTaskMessage(selectedTask.id, draft);
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Could not send message.';
      setFeedback({ message, tone: 'error' });
    } finally {
      setSendBusy(false);
    }
  }

  async function handleEnableForUser() {
    setFeedback(null);
    try {
      await updateFeatures({ controlCentre: true });
      await refresh();
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Could not update feature access.';
      setFeedback({ message, tone: 'error' });
    }
  }

  async function handleRunTask() {
    if (!selectedTask || runBusy) return;
    setRunBusy(true);
    setFeedback(null);
    try {
      const result = await callRunAgentTask(selectedTask.id);
      if (result.status === 'failed') {
        setFeedback({ message: 'Task runtime failed. Open the thread for details.', tone: 'error' });
      } else if (result.status === 'needs_confirmation') {
        setFeedback({ message: 'Task paused: browser high-impact actions require approval.', tone: 'info' });
      }
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Could not run task right now.';
      setFeedback({ message, tone: 'error' });
    } finally {
      setRunBusy(false);
    }
  }

  async function handleConfirmBrowserActions() {
    if (!selectedTask || confirmBusy) return;
    setConfirmBusy(true);
    setFeedback(null);
    try {
      await callConfirmBrowserTaskActions(selectedTask.id);
      setFeedback({ message: 'Browser actions approved. Click Run to continue.', tone: 'info' });
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Could not approve browser actions right now.';
      setFeedback({ message, tone: 'error' });
    } finally {
      setConfirmBusy(false);
    }
  }

  async function handleScheduleTask(delayMinutes: number) {
    if (!selectedTask || scheduleBusy) return;
    setScheduleBusy(true);
    setFeedback(null);
    try {
      const response = await callScheduleAgentTask(selectedTask.id, { delayMinutes });
      const formatted = new Date(response.scheduledAtMs).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      setFeedback({ message: `Task queued for backend dispatch at ${formatted}.`, tone: 'info' });
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Could not schedule task right now.';
      setFeedback({ message, tone: 'error' });
    } finally {
      setScheduleBusy(false);
    }
  }

  async function handleSaveTaskEdits() {
    if (!selectedTask || editBusy) return;
    setEditBusy(true);
    setFeedback(null);
    try {
      await updateTaskDetails(selectedTask.id, {
        title: editTitle,
        description: editDescription,
      });
      setIsEditingTask(false);
      setFeedback({ message: 'Task details updated.', tone: 'info' });
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Could not save task details.';
      setFeedback({ message, tone: 'error' });
    } finally {
      setEditBusy(false);
    }
  }

  async function handleDeleteTask() {
    if (!taskPendingDelete || deleteBusy) return;
    setDeleteBusy(true);
    setFeedback(null);
    try {
      await deleteTask(taskPendingDelete.id);
      setTaskPendingDelete(null);
      setIsEditingTask(false);
      setFeedback({ message: 'Task deleted.', tone: 'info' });
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Could not delete task.';
      setFeedback({ message, tone: 'error' });
    } finally {
      setDeleteBusy(false);
    }
  }

  if (gateLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full animate-pulse" style={{ background: 'var(--color-surface)' }} />
      </div>
    );
  }

  if (!gate.available) {
    const copy = reasonCopy(gate.reason);
    const showUserEnableAction =
      gate.reason === 'user_disabled' && gate.gates.buildEnabled && gate.gates.backendEnabled && !gate.gates.killSwitchActive;
    return (
      <div className="min-h-screen bg-[var(--color-background)] px-5 py-8">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <ArrowLeft size={16} />
            <span>Back to home</span>
          </button>
          <div className="flank-card rounded-2xl p-5 mt-4">
            <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">{copy.title}</h1>
            <p className="text-sm text-[var(--color-text-secondary)] mt-2">{copy.body}</p>
            {showUserEnableAction && (
              <button
                onClick={handleEnableForUser}
                className="flank-btn flank-btn-primary mt-4"
              >
                Enable for this account
              </button>
            )}
          </div>
          {feedback && (
            <p className="text-sm mt-3" style={{ color: 'var(--color-error)' }}>
              {feedback.message}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <header className="px-5 pt-5 pb-3 border-b border-[var(--color-border)] flex items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
            <Bot size={17} />
            Agent Control Centre
          </h1>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            Phase 4 runtime: API/MCP + browser rail safety gating
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flank-btn flank-btn-primary text-sm"
        >
          <span className="inline-flex items-center gap-1.5">
            <Plus size={14} />
            <span>New task</span>
          </span>
        </button>
      </header>

      <main className="px-5 py-5">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          <section className="flank-card rounded-2xl p-3">
            <div className="px-2 pb-2">
              <h2 className="text-xs uppercase tracking-wider text-[var(--color-text-muted)]">Tasks</h2>
            </div>
            {loading ? (
              <div className="px-2 py-8 flex justify-center">
                <div className="w-6 h-6 rounded-full animate-pulse" style={{ background: 'var(--color-surface)' }} />
              </div>
            ) : sortedTasks.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)] px-2 py-4">No tasks yet. Create your first one.</p>
            ) : (
              <div className="flex flex-col gap-1">
                {sortedTasks.map(task => (
                  <div key={task.id} className="group relative">
                    <button
                      onClick={() => setSelectedTaskId(task.id)}
                      className="w-full text-left rounded-xl px-3 py-2 pr-8 transition-colors border"
                      style={{
                        background: selectedTaskId === task.id ? 'var(--color-surface-elevated)' : 'transparent',
                        borderColor: selectedTaskId === task.id ? 'var(--color-brand-primary)' : 'transparent',
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{task.title}</p>
                        <span className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-secondary)] shrink-0">
                          {STATUS_ICON[task.status]}
                          {STATUS_LABEL[task.status]}
                        </span>
                      </div>
                      {task.description && (
                        <p className="text-xs text-[var(--color-text-muted)] mt-1 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); setTaskPendingDelete(task); }}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-surface)]"
                      aria-label={`Delete ${task.title}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="flank-card rounded-2xl p-4 min-h-[480px] flex flex-col">
            {!selectedTask ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-sm text-[var(--color-text-muted)]">Select a task to view its thread.</p>
              </div>
            ) : (
              <>
                <div className="pb-3 border-b border-[var(--color-border)] flex flex-col gap-2">
                  {/* Title + description — full width */}
                  <div>
                    {isEditingTask ? (
                      <div className="space-y-2">
                        <input
                          value={editTitle}
                          onChange={(event) => setEditTitle(event.target.value)}
                          placeholder="Task title"
                          className="w-full rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                          style={{ borderColor: 'var(--color-border)' }}
                          autoFocus
                        />
                        <textarea
                          value={editDescription}
                          onChange={(event) => setEditDescription(event.target.value)}
                          placeholder="Task description"
                          rows={3}
                          className="w-full rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)] resize-none"
                          style={{ borderColor: 'var(--color-border)' }}
                        />
                      </div>
                    ) : (
                      <>
                        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{selectedTask.title}</h2>
                        {selectedTask.description && (
                          <p className="text-sm text-[var(--color-text-secondary)] mt-1">{selectedTask.description}</p>
                        )}
                      </>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedTask.runtime && (
                        <span className="text-[11px] px-2 py-1 rounded-md bg-[var(--color-surface)] text-[var(--color-text-secondary)] border border-[var(--color-border)]">
                          Rail: {selectedTask.runtime.route} · Stage: {selectedTask.runtime.stage}
                        </span>
                      )}
                      {selectedTask.runtime?.permissionTier && (
                        <span className="text-[11px] px-2 py-1 rounded-md bg-[var(--color-surface)] text-[var(--color-text-secondary)] border border-[var(--color-border)]">
                          Tier: {selectedTask.runtime.permissionTier}
                        </span>
                      )}
                      {selectedTask.runtime?.scheduledFor && (
                        <span className="text-[11px] px-2 py-1 rounded-md bg-[var(--color-surface)] text-[var(--color-text-secondary)] border border-[var(--color-border)]">
                          Scheduled: {new Date(selectedTask.runtime.scheduledFor.toMillis()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      {selectedTask.runtime && selectedTask.runtime.estimatedCostUsd > 0 && (
                        <span className="text-[11px] px-2 py-1 rounded-md bg-[var(--color-surface)] text-[var(--color-text-secondary)] border border-[var(--color-border)]">
                          Est. Cost: ${selectedTask.runtime.estimatedCostUsd.toFixed(4)}
                        </span>
                      )}
                      {selectedTask.permissionState && (
                        <span className="text-[11px] px-2 py-1 rounded-md bg-[var(--color-surface)] text-[var(--color-text-secondary)] border border-[var(--color-border)]">
                          Permissions: {selectedTask.permissionState.state}
                        </span>
                      )}
                      {selectedTask.runtime?.confirmationRequired && (
                        <span className="text-[11px] px-2 py-1 rounded-md bg-[var(--color-warning-subtle)] text-[var(--color-warning)] border border-[var(--color-warning)]">
                          Awaiting confirmation
                        </span>
                      )}
                    </div>
                    {selectedTask.runtime?.confirmationRequired && selectedTask.runtime?.confirmationMessage && (
                      <p className="text-xs mt-2 text-[var(--color-text-secondary)]">
                        {selectedTask.runtime.confirmationMessage}
                      </p>
                    )}
                    {selectedTask.runtime?.safetyFindings && selectedTask.runtime.safetyFindings.length > 0 && (
                      <div className="mt-2 rounded-lg border border-[var(--color-warning)] bg-[var(--color-warning-subtle)] p-2">
                        <p className="text-[11px] uppercase tracking-wide text-[var(--color-warning)]">
                          Safety findings
                        </p>
                        <div className="mt-1 flex flex-col gap-1">
                          {selectedTask.runtime.safetyFindings.slice(0, 4).map((finding, index) => (
                            <p key={`${index}-${finding}`} className="text-xs text-[var(--color-text-primary)]">
                              {finding}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedTask.runtime?.reviewUrls && selectedTask.runtime.reviewUrls.length > 0 && (
                      <div className="mt-2">
                        <p className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                          Review URLs
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {selectedTask.runtime.reviewUrls.slice(0, 4).map((url) => (
                            <a
                              key={url}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] px-2 py-1 rounded-md border border-[var(--color-border)] text-[var(--color-text-secondary)] bg-[var(--color-surface)] hover:text-[var(--color-text-primary)]"
                            >
                              {url}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedTask.runtime?.screenshotUrls && selectedTask.runtime.screenshotUrls.length > 0 && (
                      <div className="mt-2">
                        <p className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                          Screenshot evidence
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {selectedTask.runtime.screenshotUrls.slice(0, 4).map((url) => (
                            <a
                              key={url}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] px-2 py-1 rounded-md border border-[var(--color-border)] text-[var(--color-text-secondary)] bg-[var(--color-surface)] hover:text-[var(--color-text-primary)]"
                            >
                              Open screenshot
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedTask.runtime?.confirmationRequired
                      && (!selectedTask.runtime.screenshotUrls || selectedTask.runtime.screenshotUrls.length === 0) && (
                      <p className="text-[11px] mt-2 text-[var(--color-text-muted)]">
                        Browser screenshot evidence is currently pending live executor capture.
                      </p>
                    )}
                    <div className="mt-2">
                      <p className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                        Planned tool calls
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {plannedToolCalls.length === 0 ? (
                          <span className="text-[11px] px-2 py-1 rounded-md border border-[var(--color-border)] text-[var(--color-text-muted)]">
                            No API/MCP tool plan for this rail
                          </span>
                        ) : plannedToolCalls.map((toolName) => (
                          <span
                            key={toolName}
                            className="text-[11px] px-2 py-1 rounded-md border border-[var(--color-border)] text-[var(--color-text-secondary)] bg-[var(--color-surface)]"
                          >
                            {toolName}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons — own row, full width, left-aligned */}
                  <div className="flex flex-wrap items-center gap-2">
                    {isEditingTask ? (
                      <>
                        <button
                          onClick={handleSaveTaskEdits}
                          className="flank-btn flank-btn-primary text-xs"
                          disabled={editBusy || !editTitle.trim()}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {editBusy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            <span>Save</span>
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingTask(false);
                            setEditTitle(selectedTask.title);
                            setEditDescription(selectedTask.description);
                          }}
                          className="flank-btn flank-btn-secondary text-xs"
                          disabled={editBusy}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <X size={14} />
                            <span>Cancel</span>
                          </span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setIsEditingTask(true);
                            setEditTitle(selectedTask.title);
                            setEditDescription(selectedTask.description);
                          }}
                          className="flank-btn flank-btn-secondary text-xs"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <Pencil size={14} />
                            <span>Edit</span>
                          </span>
                        </button>
                        <button
                          onClick={() => setTaskPendingDelete(selectedTask)}
                          className="flank-btn flank-btn-secondary text-xs"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <Trash2 size={14} />
                            <span>Delete</span>
                          </span>
                        </button>
                        <div className="h-4 w-px mx-1" style={{ background: 'var(--color-border)' }} />
                        {selectedTask.runtime?.confirmationRequired && (
                          <button
                            onClick={handleConfirmBrowserActions}
                            className="flank-btn flank-btn-secondary text-xs"
                            disabled={confirmBusy}
                          >
                            <span className="inline-flex items-center gap-1.5">
                              {confirmBusy ? <Loader2 size={14} className="animate-spin" /> : <CircleAlert size={14} />}
                              <span>Approve browser actions</span>
                            </span>
                          </button>
                        )}
                        <button
                          onClick={() => handleScheduleTask(10)}
                          className="flank-btn flank-btn-secondary text-xs"
                          disabled={scheduleBusy || selectedTask.status === 'active' || confirmBusy}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {scheduleBusy ? <Loader2 size={14} className="animate-spin" /> : <CircleDashed size={14} />}
                            <span>Schedule +10m</span>
                          </span>
                        </button>
                        <button
                          onClick={handleRunTask}
                          className="flank-btn flank-btn-primary text-xs"
                          disabled={runBusy || scheduleBusy || confirmBusy || selectedTask.status === 'active'}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {runBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                            <span>{selectedTask.status === 'completed' ? 'Re-run' : 'Run'}</span>
                          </span>
                        </button>
                        <select
                          value={selectedTask.status}
                          onChange={async (event) => {
                            await updateStatus(selectedTask.id, event.target.value as AgentTaskStatus);
                          }}
                          className="rounded-lg border px-2 py-1.5 text-xs bg-[var(--color-background)] text-[var(--color-text-primary)]"
                          style={{ borderColor: 'var(--color-border)' }}
                          aria-label="Task status"
                        >
                          <option value="pending">Pending</option>
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="failed">Failed</option>
                        </select>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto py-4 pr-1">
                  <div className="mb-3 rounded-xl border border-[var(--color-border)] p-3 bg-[var(--color-surface)]">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                        Executed tool calls
                      </p>
                      {toolCallsLoading && <Loader2 size={12} className="animate-spin text-[var(--color-text-muted)]" />}
                    </div>
                    {toolCalls.length === 0 ? (
                      <p className="text-xs text-[var(--color-text-muted)] mt-2">
                        No tool calls recorded yet for this task.
                      </p>
                    ) : (() => {
                      // Group by jobId, preserving insertion order (newest first from subscription)
                      const groups = new Map<string, typeof toolCalls>();
                      for (const audit of toolCalls) {
                        const key = audit.jobId ?? '_ungrouped';
                        if (!groups.has(key)) groups.set(key, []);
                        groups.get(key)!.push(audit);
                      }
                      const groupEntries = [...groups.entries()];
                      return (
                        <div className="mt-2 flex flex-col gap-3">
                          {groupEntries.map(([jobKey, audits], groupIndex) => (
                            <div key={jobKey}>
                              <p className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] mb-1">
                                Run {groupEntries.length - groupIndex}
                                {jobKey !== '_ungrouped' && (
                                  <span className="ml-1 font-mono opacity-60">{jobKey.slice(-6)}</span>
                                )}
                              </p>
                              <div className="flex flex-col gap-1">
                                {[...audits].sort((a, b) => a.startedAtMs - b.startedAtMs).map((audit) => (
                                  <div
                                    key={audit.id}
                                    className="rounded-lg border px-2 py-1.5 text-xs text-[var(--color-text-secondary)] bg-[var(--color-background)]"
                                    style={{
                                      borderColor: audit.status === 'failed' || audit.status === 'timeout'
                                        ? 'var(--color-error)'
                                        : 'var(--color-border)',
                                    }}
                                  >
                                    <span className="font-medium text-[var(--color-text-primary)]">{audit.toolName}</span>
                                    {' · '}
                                    <span className={audit.status === 'failed' ? 'text-[var(--color-error)]' : ''}>{audit.status}</span>
                                    {' · '}
                                    <span>{audit.durationMs}ms</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  {threadLoading ? (
                    <div className="py-10 flex justify-center">
                      <div className="w-6 h-6 rounded-full animate-pulse" style={{ background: 'var(--color-surface)' }} />
                    </div>
                  ) : thread.length === 0 ? (
                    <p className="text-sm text-[var(--color-text-muted)]">No thread messages yet.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {thread.map((message, idx) => {
                        const isLatestAgentResult = message.role === 'agent'
                          && message.eventType === 'tool_result'
                          && idx === thread.length - 2; // agent result is second to last (before completion status)
                        const screenshotUrls = selectedTask.runtime?.screenshotUrls ?? [];
                        return (
                          <div
                            key={message.id}
                            className="rounded-xl px-3 py-2 border"
                            style={{
                              borderColor: 'var(--color-border)',
                              background: message.role === 'user'
                                ? 'var(--color-surface-elevated)'
                                : message.role === 'agent'
                                  ? 'color-mix(in srgb, var(--color-brand-primary) 9%, transparent)'
                                  : 'var(--color-surface)',
                            }}
                          >
                            <p className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)] mb-1">
                              {message.role}
                            </p>
                            <p className="text-sm text-[var(--color-text-primary)] whitespace-pre-wrap">
                              {message.content}
                            </p>
                            {isLatestAgentResult && screenshotUrls.length > 0 && (
                              <div className="mt-3 flex flex-col gap-2">
                                <p className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                                  Page screenshot
                                </p>
                                {screenshotUrls.slice(0, 1).map((url) => (
                                  <a key={url} href={url} target="_blank" rel="noreferrer">
                                    <img
                                      src={url}
                                      alt="Page screenshot"
                                      className="rounded-lg border max-h-48 object-cover object-top w-full"
                                      style={{ borderColor: 'var(--color-border)' }}
                                      loading="lazy"
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <form onSubmit={handleSendMessage} className="pt-3 border-t border-[var(--color-border)] flex flex-col gap-2">
                  {sendBusy && (
                    <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)] px-1">
                      <Loader2 size={12} className="animate-spin shrink-0" />
                      <span>Agent is working — searching and reasoning…</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      value={messageDraft}
                      onChange={event => setMessageDraft(event.target.value)}
                      placeholder={sendBusy ? 'Agent is working…' : 'Ask the agent to do something…'}
                      disabled={sendBusy}
                      className="flex-1 rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)] disabled:opacity-50"
                      style={{ borderColor: 'var(--color-border)' }}
                    />
                    <button
                      type="submit"
                      className="flank-btn flank-btn-primary"
                      disabled={sendBusy || !messageDraft.trim()}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {sendBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        <span>{sendBusy ? 'Working…' : 'Send'}</span>
                      </span>
                    </button>
                  </div>
                </form>
              </>
            )}
          </section>
        </div>
      </main>

      {feedback && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 max-w-sm px-4 py-3 rounded-xl text-sm shadow-lg z-50"
          style={{
            background: feedback.tone === 'error'
              ? 'var(--color-error)'
              : 'color-mix(in srgb, var(--color-brand-primary) 80%, black 20%)',
            color: 'white',
            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.35)',
          }}
          onClick={() => setFeedback(null)}
        >
          {feedback.message}
        </div>
      )}

      {taskPendingDelete && (
        <div className="fixed inset-0 bg-black/45 flex items-end sm:items-center justify-center z-50 p-4">
          <div
            className="w-full max-w-md rounded-2xl p-5 flex flex-col gap-3"
            style={{ background: 'var(--color-background)' }}
          >
            <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Delete task?</h3>
            <p className="text-sm text-[var(--color-text-secondary)]">
              This removes <span className="font-medium text-[var(--color-text-primary)]">{taskPendingDelete.title}</span> from this list.
            </p>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                className="flank-btn flank-btn-secondary"
                onClick={() => {
                  if (deleteBusy) return;
                  setTaskPendingDelete(null);
                }}
              >
                <span className="inline-flex items-center gap-1.5">
                  <X size={14} />
                  <span>Cancel</span>
                </span>
              </button>
              <button
                type="button"
                className="flank-btn flank-btn-primary"
                onClick={handleDeleteTask}
                disabled={deleteBusy}
                style={{
                  background: 'var(--color-error)',
                  borderColor: 'var(--color-error)',
                }}
              >
                <span className="inline-flex items-center gap-1.5">
                  {deleteBusy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  <span>Delete</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/45 flex items-end sm:items-center justify-center z-50 p-4">
          <form
            onSubmit={handleCreateTask}
            className="w-full max-w-lg rounded-2xl p-5 flex flex-col gap-3"
            style={{ background: 'var(--color-background)' }}
          >
            <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Create task</h3>
            <input
              value={createTitle}
              onChange={(event) => setCreateTitle(event.target.value)}
              placeholder="Task title"
              className="rounded-lg border px-3 py-2 text-sm bg-[var(--color-surface)] text-[var(--color-text-primary)]"
              style={{ borderColor: 'var(--color-border)' }}
              autoFocus
            />
            <textarea
              value={createDescription}
              onChange={(event) => setCreateDescription(event.target.value)}
              placeholder="Optional description"
              rows={4}
              className="rounded-lg border px-3 py-2 text-sm bg-[var(--color-surface)] text-[var(--color-text-primary)] resize-none"
              style={{ borderColor: 'var(--color-border)' }}
            />
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                className="flank-btn flank-btn-secondary"
                onClick={() => {
                  if (createBusy) return;
                  setShowCreateModal(false);
                }}
              >
                <span className="inline-flex items-center gap-1.5">
                  <X size={14} />
                  <span>Cancel</span>
                </span>
              </button>
              <button
                type="submit"
                className="flank-btn flank-btn-primary"
                disabled={createBusy || !createTitle.trim()}
              >
                <span className="inline-flex items-center gap-1.5">
                  {createBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  <span>Create</span>
                </span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
